import { useState, useEffect, useRef, Fragment } from 'react';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import 'photoswipe/style.css';
import { useParams } from 'react-router-dom';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  decodeProposalToken,
  fetchProposalResults,
  fetchPublishStatus,
  fetchProductById,
  fetchProject,
  updatePublishStatus,
  updateActiveStatus,
  updateSortIds,
  deleteProjectResult,
  createCloseups,
  fetchOption1Values,
  createProductCombinations,
  setProjectResultStatus,
} from '../../services/catalogueApi';

const CLOSEUP_CATEGORIES = [
  { key: 'deck_coping',     label: 'Deck / Coping' },
  { key: 'water_waterline', label: 'Water / Waterline' },
  { key: 'spa_bond_beam',   label: 'Spa / Raised Bond Beam' },
];

// Elements the user can retry/replace (target_elements).
const RETRY_ELEMENTS = ['Decking', 'Coping', 'Water', 'Waterline Tile', 'Vertical Element'];
import TopBar from '../../components/TopBar/TopBar';
import ProposalHeader from '../../components/ProposalHeader/ProposalHeader';
import ImageCard from '../../components/ImageCard/ImageCard';
import styles from './ProposalPage.module.css';


// Label shown over a card while a retry/closeup is being generated for it.
const processingLabel = (r) =>
  r?.retry_status === 'In_Progress'   ? 'Regenerating…'
  : r?.closeup_status === 'In_Progress' ? 'Generating closeup…'
  : null;

function ProcessingOverlay({ label }) {
  return (
    <div className={styles.procOverlay}>
      <i className={`bi bi-arrow-repeat ${styles.procSpin}`} /> {label}
    </div>
  );
}

function SortableImageCard({ result: r, isEditMode, isPendingRemove, isPendingDisable, isPendingEnable, isDisabled, onClose, onToggle, onUndo, onCloseup, onRetry, showFilename, index, onImageClick }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: r.result_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div ref={setNodeRef} style={{ ...style, position: 'relative' }}>
      {isEditMode && (
        <div className={styles.dragHandle} {...attributes} {...listeners}><i className="bi bi-grip-horizontal" /></div>
      )}
      {processingLabel(r) && <ProcessingOverlay label={processingLabel(r)} />}
      <ImageCard
        src={r.s3_path}
        filename={showFilename ? `result_${r.result_id}` : undefined}
        editable={isEditMode}
        onClose={onClose}
        onToggle={onToggle}
        onUndo={onUndo}
        onCloseup={onCloseup}
        onRetry={onRetry}
        isPendingRemove={isPendingRemove}
        isPendingDisable={isPendingDisable}
        isPendingEnable={isPendingEnable}
        isDisabled={isDisabled}
        index={index}
        onImageClick={onImageClick}
      />
    </div>
  );
}

function GridImageCard({ result: r, isPendingRemove, isPendingDisable, isPendingEnable, isDisabled, onToggle, onUndo, onCloseup, onRetry, index, onConfirmRemove, onImageClick }) {
  const [loaded, setLoaded]     = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: r.result_id });

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => { if (!e.target.closest('[data-gridmenu]')) setMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const cardClass = [
    styles.gridCard,
    isPendingRemove  ? styles.gridBorderRemove  : '',
    isPendingDisable ? styles.gridBorderDisable : '',
    isPendingEnable  ? styles.gridBorderEnable  : '',
  ].join(' ');
  // Image fades only on the SAVED disabled state, not on a pending toggle.
  const imgClass  = `${styles.gridImage} ${isDisabled ? styles.gridImageDisabled : ''} ${isPendingRemove ? styles.gridImagePendingRemove : ''}`;
  const effectiveDisabled = isPendingDisable ? true : isPendingEnable ? false : isDisabled;
  const hasPending = isPendingRemove || isPendingDisable || isPendingEnable;
  const sortStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <>
      <div className={cardClass} ref={setNodeRef} style={sortStyle}>
        {!loaded && <div className={styles.gridSkeleton} />}
        {processingLabel(r) && <ProcessingOverlay label={processingLabel(r)} />}
        <div className={styles.gridDragHandle} {...attributes} {...listeners} title="Drag to reorder">
          <i className="bi bi-grip-vertical" />
        </div>
        <a data-pswp-src={r.s3_path} data-pswp-width="1600" data-pswp-height="1067" style={{ display: loaded ? 'block' : 'none' }}>
          <img
            src={r.s3_path}
            alt={`result_${r.result_id}`}
            className={imgClass}
            onLoad={() => setLoaded(true)}
            style={{ width: '100%', display: 'block' }}
          />
        </a>
        {loaded && <div className={styles.gridBadge}>{index}</div>}
        <div className={styles.gridActions} data-gridmenu>
          {hasPending && (
            <button className={`${styles.gridMenuBtn} ${styles.gridUndoBtn}`} onClick={onUndo} title="Undo change" aria-label="Undo change">
              <i className="bi bi-arrow-counterclockwise" />
            </button>
          )}
          <button className={styles.gridMenuBtn} onClick={() => setMenuOpen(p => !p)}><i className="bi bi-three-dots-vertical" /></button>
          {menuOpen && (
            <div className={styles.gridDropdown} data-gridmenu>
              <button className={styles.gridDropdownItem} disabled={!hasPending} onClick={() => { setMenuOpen(false); onUndo(); }}>
                <i className={`bi bi-arrow-counterclockwise ${styles.iconUndo}`} /> Undo
              </button>
              <button className={styles.gridDropdownItem} disabled={isPendingRemove} onClick={() => { setMenuOpen(false); onRetry(); }}>
                <i className={`bi bi-stars ${styles.iconRetry}`} /> Retry
              </button>
              <button className={styles.gridDropdownItem} disabled={isPendingRemove} onClick={() => { setMenuOpen(false); onToggle(); }}>
                {effectiveDisabled
                  ? <><i className={`bi bi-eye ${styles.iconEnable}`} /> Enable</>
                  : <><i className={`bi bi-eye-slash ${styles.iconDisable}`} /> Disable</>}
              </button>
              <button className={styles.gridDropdownItem} disabled={isPendingRemove} onClick={() => { setMenuOpen(false); onCloseup(); }}>
                <i className={`bi bi-zoom-in ${styles.iconCloseup}`} /> Create Closeup
              </button>
              <div className={styles.gridDropdownDivider} />
              <button
                className={styles.gridDropdownItem}
                disabled={isPendingDisable || isPendingEnable}
                onClick={() => { setMenuOpen(false); onConfirmRemove(); }}
              >
                <i className={`bi bi-trash ${styles.iconRemove}`} /> Remove
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// A closeup rendered as its own full grid cell (same size as image cards),
// marked with a "Closeup" sign. Not sortable — it follows its parent.
function GridCloseupCard({ closeup: c }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <div className={`${styles.gridCard} ${styles.gridCloseupCard}`}>
      {!loaded && <div className={styles.gridSkeleton} />}
      <a data-pswp-src={c.s3_path} data-pswp-width="1600" data-pswp-height="1067" style={{ display: loaded ? 'block' : 'none' }}>
        <img
          src={c.s3_path}
          alt="closeup"
          className={styles.gridImage}
          onLoad={() => setLoaded(true)}
          style={{ width: '100%', display: 'block' }}
        />
      </a>
      {loaded && <div className={styles.gridCloseupBadge}><i className="bi bi-zoom-in" /> Closeup</div>}
    </div>
  );
}

function CloseupList({ closeups, editable }) {
  if (!closeups || !closeups.length) return null;
  return (
    <>
      {closeups.map((c) => (
        editable ? (
          // Edit/draft: mark it as a closeup (label + accent) so the owner knows it
          // belongs to the image above and isn't a separate, draggable result.
          <div key={c.result_id} className={styles.closeupWrap}>
            <span className={styles.closeupLabel}><i className="bi bi-zoom-in" /> Closeup</span>
            <a
              className={styles.closeupCard}
              data-pswp-src={c.s3_path}
              data-pswp-width="1600"
              data-pswp-height="1067"
            >
              <img src={c.s3_path} alt="closeup" className={styles.closeupImg} />
            </a>
          </div>
        ) : (
          // Published: render as a plain image, no closeup markings.
          <ImageCard key={c.result_id} src={c.s3_path} editable={false} />
        )
      ))}
    </>
  );
}

function Toast({ message, type, onClose, persistent }) {
  useEffect(() => {
    if (persistent) return;                 // persistent toasts stay until the user closes them
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose, persistent]);

  return (
    <div className={`${styles.toast} ${type === 'error' ? styles.toastError : styles.toastSuccess}`}>
      <i className={`bi ${type === 'error' ? 'bi-x-circle' : 'bi-check-circle'}`} /> {message}
      <button type="button" className={styles.toastClose} onClick={onClose} aria-label="Close">
        <i className="bi bi-x-lg" />
      </button>
    </div>
  );
}

function ProposalPage() {
  const { token } = useParams();
  const { projectId, productId } = decodeProposalToken(token);

  const params                       = new URLSearchParams(window.location.search);
  const [currentUserId, currentRoleId] = (params.get('user_id') || '').split('_');

  const [results, setResults]                   = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [error, setError]                       = useState(null);
  const [publishStatus, setPublishStatus]       = useState(null);
  const [productImage, setProductImage]         = useState(null);
  const [productName, setProductName]           = useState(null);
  const [productUrl, setProductUrl]             = useState(null);
  const [brandLogo, setBrandLogo]               = useState(null);
  const [component, setComponent]               = useState(null);
  const [option1, setOption1]                   = useState(null);
  const [pendingRemovals, setPendingRemovals]   = useState(new Set());
  const [pendingToggles, setPendingToggles]     = useState(new Map());
  const [hasPendingSort, setHasPendingSort]     = useState(false);
  const [isOwner, setIsOwner]                   = useState(false);
  const [isEditMode, setIsEditMode]             = useState(false);
  const [authChecked, setAuthChecked]           = useState(false);
  const [toast, setToast]                       = useState(null);
  const [saving, setSaving]                     = useState(false);
  const [viewMode, setViewMode]                 = useState('grid');
  const [gridColumns, setGridColumns]           = useState(3);
  const [gridConfirmId, setGridConfirmId]       = useState(null);
  const [closeupTarget, setCloseupTarget]       = useState(null);
  const [closeupCategory, setCloseupCategory]   = useState(null);
  const [retryTarget, setRetryTarget]           = useState(null);
  const [retryElement, setRetryElement]         = useState('');
  const [retryMaterial, setRetryMaterial]       = useState('');
  const [option1Values, setOption1Values]       = useState(null);
  const galleryRef = useRef(null);
  const pollingRef = useRef(false);
  const resultsRef = useRef([]);   // latest results, for detecting status transitions in the poll

  const sensors = useSensors(useSensor(PointerSensor));

  useEffect(() => {
    if (!galleryRef.current || !results.length) return;
    const lightbox = new PhotoSwipeLightbox({
      gallery: galleryRef.current,
      children: 'a[data-pswp-src]',
      pswpModule: () => import('photoswipe'),
    });
    lightbox.on('uiRegister', () => {
      lightbox.pswp.ui.registerElement({
        name: 'fullscreen-button',
        order: 9,
        isButton: true,
        tagName: 'button',
        html: '<svg aria-hidden="true" class="pswp__icn" viewBox="0 0 32 32" width="32" height="32"><path d="M8 8v6.2h2.2V10.2h4V8H8zm10 0v2.2h4v4h2.2V8h-6.2zM8 17.8V24h6.2v-2.2h-4v-4H8zm14 0v4h-4V24h6.2v-6.2H22z" fill="currentColor"/></svg>',
        onClick: () => {
          if (document.fullscreenElement) document.exitFullscreen();
          else document.documentElement.requestFullscreen();
        },
      });
    });
    lightbox.init();
    return () => lightbox.destroy();
  }, [results, viewMode, isEditMode]);

  useEffect(() => {
    if (authChecked && publishStatus !== null) {
      setIsEditMode(isOwner && publishStatus === 0);
    }
  }, [authChecked, publishStatus, isOwner]);

  // Lazily load the material types (Component -> [Option1]) the first time Retry opens.
  useEffect(() => {
    if (retryTarget && option1Values === null) {
      fetchOption1Values()
        .then(setOption1Values)
        .catch(() => setOption1Values({}));
    }
  }, [retryTarget, option1Values]);

  useEffect(() => {
    setLoading(true);
    setError(null);

    fetchProposalResults(projectId, productId)
      .then(data => {
        const sorted = data.sort((a, b) => a.sort_id - b.sort_id);
        setResults(sorted);
        // Resume polling if a retry/closeup was still running when the page (re)loaded,
        // so the status/overlay auto-updates without a manual refresh.
        if (sorted.some(r => r.retry_status === 'In_Progress' || r.closeup_status === 'In_Progress')) {
          pollForUpdates();
        }
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));

    fetchPublishStatus(productId)
      .then(links => setPublishStatus(links[0]?.publish_status ?? 0))
      .catch(() => setPublishStatus(0));

    fetchProductById(productId)
      .then(product => {
        setProductImage(product?.MobileImage || null);
        setProductName(product?.ProductName || null);
        setProductUrl(product?.ViewProductLink || null);
        setBrandLogo(product?.manufacturer_logo || null);
        setComponent(product?.Component || null);
        setOption1(product?.Option1 || null);
        const applyFavicon = (href) => {
          document.querySelectorAll("link[rel*='icon']").forEach(el => el.remove());
          const link = document.createElement('link');
          link.rel  = 'icon';
          link.href = href;
          document.head.appendChild(link);
        };
        const defaultFavicon = `${process.env.PUBLIC_URL}/favicon.ico`;
        if (product?.manufacturer_favicon) {
          // Probe the favicon — it may be blocked (e.g. Cloudflare). Fall back to default on failure.
          const probe = new Image();
          probe.onload  = () => applyFavicon(product.manufacturer_favicon);
          probe.onerror = () => applyFavicon(defaultFavicon);
          probe.src     = product.manufacturer_favicon;
        } else {
          applyFavicon(defaultFavicon);
        }
      })
      .catch(() => {});

    const superAdmin = Number(currentRoleId) === 1;

    if (superAdmin) {
      setIsOwner(true);
      setAuthChecked(true);
    } else {
      fetchProject(projectId)
        .then(project => {
          const owner = !!currentUserId && Number(project.user_id) === Number(currentUserId);
          setIsOwner(owner);
        })
        .catch(() => {})
        .finally(() => setAuthChecked(true));
    }
  }, [token]);

  const handlePublish = async () => {
    await updatePublishStatus(productId, 1);
    setPublishStatus(1);
    setIsEditMode(false);
  };

  const handleUnpublish = async () => {
    await updatePublishStatus(productId, 0);
    setPublishStatus(0);
    setIsEditMode(true);
  };

  const handleRemove = (resultId) => {
    setPendingRemovals(prev => {
      const next = new Set(prev);
      if (next.has(resultId)) next.delete(resultId);
      else next.add(resultId);
      return next;
    });
  };

  const handleToggle = (resultId) => {
    setPendingToggles(prev => {
      const next = new Map(prev);
      if (next.has(resultId)) next.delete(resultId);
      else {
        const r = results.find(r => r.result_id === resultId);
        next.set(resultId, r?.active_status === 1 ? 0 : 1);
      }
      return next;
    });
  };

  const handleUndo = (resultId) => {
    setPendingRemovals(prev => { const next = new Set(prev); next.delete(resultId); return next; });
    setPendingToggles(prev => { const next = new Map(prev); next.delete(resultId); return next; });
  };

  // Keep a ref of the latest results so the poll can detect status transitions.
  useEffect(() => { resultsRef.current = results; }, [results]);

  // Re-fetch results and (a) merge fresh closeup_results into existing rows and
  // (b) append any newly generated images (e.g. from a retry) — without disturbing
  // current order or pending edits.
  const pollForUpdates = () => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    let attempts = 0;
    const tick = () => {
      attempts += 1;
      let stillPending = true;   // keep polling on error/unknown; cap is the safety net
      fetchProposalResults(projectId, productId)
        .then(data => {
          const stripQuery = (u) => (u || '').split('?')[0];   // ignore presigned query string
          // Notify on a transition INTO Failed (compare against last-known status, so an
          // already-Failed row present on load doesn't fire a notification).
          const prevById = new Map(resultsRef.current.map(r => [r.result_id, r]));
          let failedMsg = null;
          data.forEach(d => {
            const was = prevById.get(d.result_id);
            if (!was) return;
            if (d.retry_status === 'Failed' && was.retry_status !== 'Failed') {
              failedMsg = 'Retry failed. Please try again.';
            } else if (d.closeup_status === 'Failed' && was.closeup_status !== 'Failed') {
              failedMsg = failedMsg || 'Closeup failed. Please try again.';
            }
          });
          if (failedMsg) setToast({ message: failedMsg, type: 'error', persistent: true });
          setResults(prev => {
            const known  = new Set(prev.map(r => r.result_id));
            const merged = prev.map(r => {
              const fresh = data.find(d => d.result_id === r.result_id);
              if (!fresh) return r;
              // A retry that REPLACES the record keeps result_id but changes the underlying
              // s3 object — swap the image in place only when the object path actually changed.
              const s3Changed = stripQuery(fresh.s3_path) !== stripQuery(r.s3_path);
              return {
                ...r,
                s3_path: s3Changed ? fresh.s3_path : r.s3_path,
                closeup_results: fresh.closeup_results,
                retry_status:    fresh.retry_status,    // clear the overlay when the worker completes
                closeup_status:  fresh.closeup_status,
              };
            });
            const additions = data
              .filter(d => !known.has(d.result_id))
              .sort((a, b) => a.sort_id - b.sort_id);
            return additions.length ? [...merged, ...additions] : merged;
          });
          // Stop early only when the API reports statuses AND nothing is still In_Progress.
          // If the status fields aren't present, fall back to polling until the cap.
          const hasStatus = data.some(d => d.retry_status !== undefined || d.closeup_status !== undefined);
          const pending   = data.some(d => d.retry_status === 'In_Progress' || d.closeup_status === 'In_Progress');
          stillPending = pending || !hasStatus;
        })
        .catch(() => {})
        .finally(() => {
          if (attempts < 30 && stillPending) setTimeout(tick, 10000);   // up to ~5 min, stops early when done
          else pollingRef.current = false;
        });
    };
    setTimeout(tick, 8000);   // first check after ~8s
  };

  const handleCloseup = async (result, category) => {
    try {
      await createCloseups({
        element_category: category,
        items: [{
          product_id:  result.product_id,
          combination: result.combination,
          image_id:    result.image_id,
        }],
      });
      // Flag the source image as having an in-progress closeup (worker sets it 'completed' on save).
      if (result.unique_id != null) {
        try { await setProjectResultStatus(result.unique_id, { closeup_status: 'In_Progress' }); }
        catch (e) { /* non-fatal — status is cosmetic */ }
        setResults(prev => prev.map(r => r.result_id === result.result_id ? { ...r, closeup_status: 'In_Progress' } : r));
      }
      setToast({
        message: 'Closeup requested! It’s being generated and will appear here automatically in a little while.',
        type: 'success',
      });
      pollForUpdates();
    } catch (err) {
      setToast({ message: `Closeup failed: ${err.message}`, type: 'error' });
    }
  };

  const handleRetry = async (result, element, material) => {
    // Primary element = the proposal product's own Component → regenerate with the CURRENT product
    // (no material search). The use_primary_product flag tells the backend to skip the search.
    const isPrimary = !!component && element.toLowerCase() === String(component).trim().toLowerCase();
    try {
      await createProductCombinations({
        project_id: Number(projectId),
        image_ids:  [result.unique_id],   // key is "image_ids" but value is unique_id (exact output render)
        ...(isPrimary ? { use_primary_product: true } : { product_type: material }),
        products: [{
          product_id:      String(result.product_id),
          source_element:  component || result.component || '',
          target_elements: [element],
        }],
      });
      // Flag this output as retrying (worker sets it 'completed' when the new render is saved).
      if (result.unique_id != null) {
        try { await setProjectResultStatus(result.unique_id, { retry_status: 'In_Progress' }); }
        catch (e) { /* non-fatal — status is cosmetic */ }
        setResults(prev => prev.map(r => r.result_id === result.result_id ? { ...r, retry_status: 'In_Progress' } : r));
      }
      setToast({
        message: isPrimary
          ? `Retry requested — regenerating ${element} with the current product. The new image will appear here automatically in a little while.`
          : `Retry requested — replacing ${element} with ${material}. The new image will appear here automatically in a little while.`,
        type: 'success',
      });
      pollForUpdates();
    } catch (err) {
      setToast({ message: `Retry failed: ${err.message}`, type: 'error' });
    }
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setResults(prev => {
      const oldIndex = prev.findIndex(r => r.result_id === active.id);
      const newIndex = prev.findIndex(r => r.result_id === over.id);
      setHasPendingSort(true);
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      if (pendingRemovals.size > 0) {
        const toRemove = results.filter(r => pendingRemovals.has(r.result_id));
        await Promise.all(
          toRemove.map(r =>
            deleteProjectResult({
              project_id:  r.project_id,
              product_id:  r.product_id,
              combination: r.combination,
              image_id:    r.image_id,
            })
          )
        );
        setResults(prev => prev.filter(r => !pendingRemovals.has(r.result_id)));
        setPendingRemovals(new Set());
      }

      if (pendingToggles.size > 0) {
        await Promise.all(
          Array.from(pendingToggles.entries()).map(([resultId, activeStatus]) => {
            const r = results.find(r => r.result_id === resultId);
            return updateActiveStatus({
              project_id:    r.project_id,
              product_id:    r.product_id,
              combination:   r.combination,
              image_id:      r.image_id,
              active_status: activeStatus,
            });
          })
        );
        setResults(prev => prev.map(r =>
          pendingToggles.has(r.result_id)
            ? { ...r, active_status: pendingToggles.get(r.result_id) }
            : r
        ));
        setPendingToggles(new Map());
      }

      if (hasPendingSort) {
        const remaining = results.filter(r => !pendingRemovals.has(r.result_id));
        const entries = remaining.map((r, i) => ({
          unique_id:  r.unique_id,
          project_id: r.project_id,
          product_id: r.product_id,
          sort_id:    i + 1,
        }));
        await updateSortIds(entries);
        setHasPendingSort(false);
      }

      setToast({ message: 'Changes saved successfully.', type: 'success' });
    } catch (err) {
      setToast({ message: `Save failed: ${err.message}`, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const hasPendingChanges = pendingRemovals.size > 0 || pendingToggles.size > 0 || hasPendingSort;
  const myPoolUrl = projectId
    ? `https://config.poolpix.ai/?projectId=${btoa(String(projectId))}`
    : null;

  if (loading || !authChecked) {
    return <div className={styles.notFound}><p>Loading…</p></div>;
  }

  if (error) {
    return <div className={styles.notFound}><p>Error: {error}</p></div>;
  }

  if (publishStatus === 0 && !isOwner) {
    return (
      <div className={styles.page}>
        <div className={styles.accentBar} />
        <div className={styles.notFound}>
          <div className={styles.inactiveCard}>
            <div className={styles.inactiveIcon}><i className="bi bi-slash-circle" /></div>
            <h1 className={styles.inactiveTitle}>Inactive Link</h1>
            <p className={styles.inactiveText}>This page is either removed or disabled for now.</p>
          </div>
        </div>
        <footer className={styles.footer}>Powered by <strong>PoolPix</strong> — AI-powered pool design</footer>
      </div>
    );
  }

  // In edit mode the owner sees all images (to enable/disable); otherwise only active ones.
  const visibleResults = isEditMode ? results : results.filter(r => r.active_status === 1);

  if (!visibleResults.length) {
    return (
      <div className={styles.page}>
        <div className={styles.accentBar} />
        <div className={styles.notFound}><p>No results for product {productId}.</p></div>
      </div>
    );
  }

  const imageCards = visibleResults.map((r, i) => (
    <div key={r.result_id}>
      <SortableImageCard
        result={r}
        isEditMode={isEditMode}
        isPendingRemove={pendingRemovals.has(r.result_id)}
        isPendingDisable={pendingToggles.get(r.result_id) === 0}
        isPendingEnable={pendingToggles.get(r.result_id) === 1}
        isDisabled={r.active_status === 0}
        onClose={() => handleRemove(r.result_id)}
        onToggle={() => handleToggle(r.result_id)}
        onUndo={() => handleUndo(r.result_id)}
        onCloseup={() => setCloseupTarget(r)}
        onRetry={() => setRetryTarget(r)}
        showFilename={isEditMode}
        index={i + 1}
      />
      <CloseupList closeups={(r.closeup_results || []).filter(c => isEditMode || c.active_status !== 0)} editable={isEditMode} />
    </div>
  ));

  return (
    <div className={styles.page}>
      <div className={`${styles.accentBar} ${isOwner ? styles.accentBarOwner : ''}`}>
        {isOwner && isEditMode && (
          <div className={styles.headerLeft}>
            <div className={styles.viewToggle}>
              <button
                className={`${styles.viewBtn} ${viewMode === 'grid' ? styles.viewBtnActive : ''}`}
                onClick={() => setViewMode('grid')}
              ><i className="bi bi-grid-3x3-gap-fill" /> Grid</button>
              <button
                className={`${styles.viewBtn} ${viewMode === 'scroll' ? styles.viewBtnActive : ''}`}
                onClick={() => setViewMode('scroll')}
              ><i className="bi bi-list-ul" /> Scroll</button>
            </div>
            {viewMode === 'grid' && (
              <div className={styles.columnsControl} aria-label="Grid columns">
                <i className="bi bi-layout-three-columns" />
                <input
                  type="range" min={2} max={5} value={gridColumns}
                  onChange={e => setGridColumns(Number(e.target.value))}
                  className={styles.accentSlider}
                />
                <span className={styles.accentSliderLabel}>{gridColumns}</span>
              </div>
            )}
          </div>
        )}
        {isOwner && (
          <div className={styles.headerRight}>
            <TopBar
              publishStatus={publishStatus}
              onPublish={handlePublish}
              onUnpublish={handleUnpublish}
              isOwner={isOwner}
              isEditMode={isEditMode}
              onEdit={() => setIsEditMode(true)}
              onDone={() => setIsEditMode(false)}
            />
          </div>
        )}
      </div>
      <div className={styles.content} style={isEditMode && viewMode === 'grid' ? { maxWidth: '1400px' } : {}}>
        <ProposalHeader
          productName={productName || `Product ${productId}`}
          productImage={productImage}
          category={component}
          subtitle={option1}
          productUrl={productUrl}
          myPoolUrl={myPoolUrl}
          brandLogo={brandLogo}
        />
        {isEditMode && viewMode === 'grid' ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={results.map(r => r.result_id)} strategy={rectSortingStrategy}>
              <div className={styles.gridList} ref={galleryRef} style={{ gridTemplateColumns: `repeat(${gridColumns}, 1fr)` }}>
                {results.map((r, i) => (
                  <Fragment key={r.result_id}>
                    <GridImageCard
                      result={r}
                      isPendingRemove={pendingRemovals.has(r.result_id)}
                      isPendingDisable={pendingToggles.get(r.result_id) === 0}
                      isPendingEnable={pendingToggles.get(r.result_id) === 1}
                      isDisabled={r.active_status === 0}
                      onToggle={() => handleToggle(r.result_id)}
                      onUndo={() => handleUndo(r.result_id)}
                      onCloseup={() => setCloseupTarget(r)}
                      onRetry={() => setRetryTarget(r)}
                      onConfirmRemove={() => setGridConfirmId(r.result_id)}
                      index={i + 1}
                      onImageClick={undefined}
                    />
                    {(r.closeup_results || []).map(c => (
                      <GridCloseupCard key={c.result_id} closeup={c} />
                    ))}
                  </Fragment>
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : isEditMode ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={results.map(r => r.result_id)} strategy={verticalListSortingStrategy}>
              <div className={styles.imageList} ref={galleryRef}>{imageCards}</div>
            </SortableContext>
          </DndContext>
        ) : (
          <div className={styles.imageList} ref={galleryRef}>{imageCards}</div>
        )}

        {gridConfirmId && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: '#fff', borderRadius: 12, padding: '24px 28px', width: 280, textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
              <p style={{ fontSize: 15, color: '#333', marginBottom: 20, fontWeight: 500 }}>Remove this image?</p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                <button onClick={() => setGridConfirmId(null)} style={{ padding: '8px 20px', border: '1.5px solid #d0d0d0', borderRadius: 8, background: '#f5f5f5', cursor: 'pointer' }}>Cancel</button>
                <button onClick={() => { handleRemove(gridConfirmId); setGridConfirmId(null); }} style={{ padding: '8px 20px', border: 'none', borderRadius: 8, background: '#ef4444', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Remove</button>
              </div>
            </div>
          </div>
        )}

        {closeupTarget && (
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => { setCloseupTarget(null); setCloseupCategory(null); }}
          >
            <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: '22px 24px', width: 320, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
              <p style={{ fontSize: 15, color: '#333', marginBottom: 16, fontWeight: 600, textAlign: 'center' }}>Create closeup of…</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}>
                {CLOSEUP_CATEGORIES.map(cat => {
                  const selected = closeupCategory === cat.key;
                  return (
                    <button
                      key={cat.key}
                      onClick={() => setCloseupCategory(cat.key)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
                        border: `1.5px solid ${selected ? '#0d9488' : '#d8e6e3'}`, borderRadius: 8,
                        background: selected ? '#0d9488' : '#f4fbfa', color: selected ? '#fff' : '#0f766e',
                        fontSize: 14, fontWeight: 600, cursor: 'pointer',
                      }}
                    >
                      <i className="bi bi-zoom-in" /> {cat.label}
                      {selected && <i className="bi bi-check-lg" style={{ marginLeft: 'auto' }} />}
                    </button>
                  );
                })}
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                <button
                  onClick={() => { setCloseupTarget(null); setCloseupCategory(null); }}
                  style={{ padding: '9px 22px', border: '1.5px solid #d0d0d0', borderRadius: 8, background: '#f5f5f5', color: '#555', fontSize: 14, cursor: 'pointer' }}
                >Cancel</button>
                <button
                  disabled={!closeupCategory}
                  onClick={() => { const t = closeupTarget; const cat = closeupCategory; setCloseupTarget(null); setCloseupCategory(null); handleCloseup(t, cat); }}
                  style={{
                    padding: '9px 22px', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600,
                    background: closeupCategory ? '#0d9488' : '#a7d8d2', color: '#fff',
                    cursor: closeupCategory ? 'pointer' : 'default',
                  }}
                >Create Closeup</button>
              </div>
            </div>
          </div>
        )}

        {retryTarget && (() => {
          // Primary element = the product's own Component → regenerate with the current product
          // (no material). Otherwise pick a material to replace it with.
          const isPrimary  = !!retryElement && !!component && retryElement.toLowerCase() === String(component).trim().toLowerCase();
          const canSubmit  = retryElement && (isPrimary || retryMaterial);
          const closeRetry = () => { setRetryTarget(null); setRetryElement(''); setRetryMaterial(''); };
          return (
            <div
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onClick={closeRetry}
            >
              <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: '22px 24px', width: 340, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
                <p style={{ fontSize: 15, color: '#333', marginBottom: 16, fontWeight: 600, textAlign: 'center' }}>Retry an element</p>

                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', margin: '0 0 5px' }}>Element</label>
                <select
                  value={retryElement}
                  onChange={e => { setRetryElement(e.target.value); setRetryMaterial(''); }}
                  style={{ width: '100%', padding: '9px 10px', border: '1.5px solid #cfd8e3', borderRadius: 8, fontSize: 14, marginBottom: 14, background: '#fff', cursor: 'pointer' }}
                >
                  <option value="">Select element…</option>
                  {RETRY_ELEMENTS.map(el => <option key={el} value={el}>{el}</option>)}
                </select>

                {!isPrimary && (
                  <>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', margin: '0 0 5px' }}>New material</label>
                    <select
                      value={retryMaterial}
                      onChange={e => setRetryMaterial(e.target.value)}
                      disabled={!retryElement}
                      style={{ width: '100%', padding: '9px 10px', border: '1.5px solid #cfd8e3', borderRadius: 8, fontSize: 14, marginBottom: 8, background: retryElement ? '#fff' : '#f1f5f9', cursor: retryElement ? 'pointer' : 'default' }}
                    >
                      <option value="">{option1Values === null ? 'Loading…' : 'Select material…'}</option>
                      {(option1Values?.[retryElement] || []).map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                    {retryElement && option1Values && (option1Values[retryElement] || []).length === 0 && (
                      <p style={{ fontSize: 12, color: '#b45309', margin: '0 0 10px' }}>No materials available for {retryElement}.</p>
                    )}
                  </>
                )}

                {isPrimary ? (
                  <p style={{ fontSize: 13, color: '#0f766e', margin: '6px 0 14px', textAlign: 'center' }}>
                    Regenerate <strong>{retryElement}</strong> with the current product
                  </p>
                ) : (retryElement && retryMaterial) ? (
                  <p style={{ fontSize: 13, color: '#0f766e', margin: '6px 0 14px', textAlign: 'center' }}>
                    Replace <strong>{retryElement}</strong> with <strong>{retryMaterial}</strong>
                  </p>
                ) : null}

                <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 8 }}>
                  <button
                    onClick={closeRetry}
                    style={{ padding: '9px 22px', border: '1.5px solid #d0d0d0', borderRadius: 8, background: '#f5f5f5', color: '#555', fontSize: 14, cursor: 'pointer' }}
                  >Cancel</button>
                  <button
                    disabled={!canSubmit}
                    onClick={() => { const t = retryTarget; const el = retryElement; const m = retryMaterial; closeRetry(); handleRetry(t, el, m); }}
                    style={{
                      padding: '9px 22px', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, color: '#fff',
                      background: canSubmit ? '#0d9488' : '#a7d8d2',
                      cursor: canSubmit ? 'pointer' : 'default',
                    }}
                  >Retry</button>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
      <footer className={styles.footer}>
        Powered by <strong>PoolPix</strong> — AI-powered pool design
      </footer>
      {isEditMode && hasPendingChanges && (
        <button className={styles.saveBtn} onClick={handleSaveAll} disabled={saving}>
          {saving ? <><i className="bi bi-hourglass-split" /> Saving…</> : <><i className="bi bi-floppy" /> Save Changes</>}
        </button>
      )}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          persistent={toast.persistent}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default ProposalPage;
